/**
 * Synapse Pulse System
 * Animated impulses traveling between neural nodes
 */

import * as THREE from 'three'
import gsap from 'gsap'

export class SynapsePulseSystem {
  constructor(scene, options = {}) {
    this.scene = scene
    this.pulses = []
    this.pulsePool = []
    this.maxPoolSize = 50
    
    this.options = {
      pulseColor: options.pulseColor || 0xff6b9d,
      pulseSize: options.pulseSize || 0.15,
      pulseSpeed: options.pulseSpeed || 1.5,
      trailLength: options.trailLength || 8,
      glowIntensity: options.glowIntensity || 2.0
    }

    this.pulseMaterial = new THREE.MeshBasicMaterial({
      color: this.options.pulseColor,
      transparent: true,
      opacity: 1.0
    })

    this.pulseGeometry = new THREE.SphereGeometry(
      this.options.pulseSize, 
      16, 
      16
    )

    this.trailMaterial = new THREE.LineBasicMaterial({
      color: this.options.pulseColor,
      transparent: true,
      opacity: 0.6
    })
  }

  createPulse(startPos, endPos, onComplete) {
    let pulse = this.pulsePool.pop()
    
    if (!pulse) {
      pulse = {
        mesh: new THREE.Mesh(this.pulseGeometry, this.pulseMaterial.clone()),
        trail: this.createTrail(),
        active: false,
        progress: 0
      }
      pulse.mesh.layers.enable(1)
    }

    pulse.startPos = startPos.clone()
    pulse.endPos = endPos.clone()
    pulse.controlPoint = this.calculateControlPoint(startPos, endPos)
    pulse.progress = 0
    pulse.active = true
    pulse.onComplete = onComplete
    pulse.trailPositions = []

    pulse.mesh.position.copy(startPos)
    pulse.mesh.material.opacity = 1.0
    pulse.mesh.scale.setScalar(1)
    
    this.scene.add(pulse.mesh)
    this.scene.add(pulse.trail)
    
    this.pulses.push(pulse)

    gsap.to(pulse, {
      progress: 1,
      duration: this.options.pulseSpeed,
      ease: 'power2.inOut',
      onUpdate: () => this.updatePulsePosition(pulse),
      onComplete: () => this.completePulse(pulse)
    })

    return pulse
  }

  calculateControlPoint(start, end) {
    const mid = new THREE.Vector3()
      .addVectors(start, end)
      .multiplyScalar(0.5)
    
    const direction = new THREE.Vector3().subVectors(end, start)
    const perpendicular = new THREE.Vector3(
      -direction.y,
      direction.x + direction.z,
      -direction.y
    ).normalize()

    const offset = direction.length() * 0.3
    
    return mid.add(perpendicular.multiplyScalar(offset))
  }

  updatePulsePosition(pulse) {
    if (!pulse.active) return

    const t = pulse.progress
    const pos = this.getQuadraticBezierPoint(
      pulse.startPos,
      pulse.controlPoint,
      pulse.endPos,
      t
    )

    pulse.mesh.position.copy(pos)
    
    // Update trail
    pulse.trailPositions.push(pos.clone())
    if (pulse.trailPositions.length > this.options.trailLength) {
      pulse.trailPositions.shift()
    }
    
    this.updateTrailGeometry(pulse)

    // Pulse glow effect
    const glowPhase = Math.sin(t * Math.PI)
    pulse.mesh.scale.setScalar(1 + glowPhase * 0.3)
    pulse.mesh.material.opacity = 0.7 + glowPhase * 0.3
  }

  getQuadraticBezierPoint(p0, p1, p2, t) {
    const point = new THREE.Vector3()
    const oneMinusT = 1 - t
    
    point.x = oneMinusT * oneMinusT * p0.x + 
              2 * oneMinusT * t * p1.x + 
              t * t * p2.x
    point.y = oneMinusT * oneMinusT * p0.y + 
              2 * oneMinusT * t * p1.y + 
              t * t * p2.y
    point.z = oneMinusT * oneMinusT * p0.z + 
              2 * oneMinusT * t * p1.z + 
              t * t * p2.z
    
    return point
  }

  createTrail() {
    const geometry = new THREE.BufferGeometry()
    const positions = new Float32Array(this.options.trailLength * 3)
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    
    const trail = new THREE.Line(geometry, this.trailMaterial.clone())
    trail.frustumCulled = false
    
    return trail
  }

  updateTrailGeometry(pulse) {
    const positions = pulse.trail.geometry.attributes.position.array
    
    for (let i = 0; i < this.options.trailLength; i++) {
      if (i < pulse.trailPositions.length) {
        const pos = pulse.trailPositions[pulse.trailPositions.length - 1 - i]
        positions[i * 3] = pos.x
        positions[i * 3 + 1] = pos.y
        positions[i * 3 + 2] = pos.z
      }
    }
    
    pulse.trail.geometry.attributes.position.needsUpdate = true
    pulse.trail.geometry.setDrawRange(0, pulse.trailPositions.length)
    
    // Fade trail
    pulse.trail.material.opacity = 0.6 * (1 - pulse.progress * 0.5)
  }

  completePulse(pulse) {
    pulse.active = false
    
    gsap.to(pulse.mesh.scale, {
      x: 2,
      y: 2,
      z: 2,
      duration: 0.3,
      ease: 'power2.out'
    })
    
    gsap.to(pulse.mesh.material, {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        this.scene.remove(pulse.mesh)
        this.scene.remove(pulse.trail)
        
        const index = this.pulses.indexOf(pulse)
        if (index > -1) {
          this.pulses.splice(index, 1)
        }
        
        if (this.pulsePool.length < this.maxPoolSize) {
          this.pulsePool.push(pulse)
        }
        
        if (pulse.onComplete) {
          pulse.onComplete()
        }
      }
    })
  }

  sendPulse(fromNode, toNode, cascade = false) {
    const startPos = new THREE.Vector3(
      fromNode.position.x,
      fromNode.position.y,
      fromNode.position.z
    )
    const endPos = new THREE.Vector3(
      toNode.position.x,
      toNode.position.y,
      toNode.position.z
    )

    this.createPulse(startPos, endPos, () => {
      if (cascade && toNode.connections) {
        // Cascade to connected nodes with delay
        toNode.connections.forEach((connectionId, index) => {
          setTimeout(() => {
            this.dispatchEvent('pulse-arrived', {
              nodeId: toNode.id,
              connectionId
            })
          }, index * 100)
        })
      }
    })
  }

  dispatchEvent(name, detail) {
    const event = new CustomEvent(name, { detail })
    document.dispatchEvent(event)
  }

  setColor(color) {
    this.options.pulseColor = color
    this.pulseMaterial.color.setHex(color)
    this.trailMaterial.color.setHex(color)
  }

  update(deltaTime) {
    // Additional per-frame updates if needed
    this.pulses.forEach(pulse => {
      if (pulse.active) {
        // Add subtle oscillation
        pulse.mesh.rotation.x += deltaTime * 2
        pulse.mesh.rotation.y += deltaTime * 3
      }
    })
  }

  dispose() {
    this.pulses.forEach(pulse => {
      this.scene.remove(pulse.mesh)
      this.scene.remove(pulse.trail)
      pulse.mesh.geometry.dispose()
      pulse.mesh.material.dispose()
      pulse.trail.geometry.dispose()
      pulse.trail.material.dispose()
    })
    
    this.pulses = []
    this.pulsePool = []
    this.pulseGeometry.dispose()
    this.pulseMaterial.dispose()
    this.trailMaterial.dispose()
  }
}
